import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from 'store/hooks'
import { useThunkDispatch } from 'models/thunk'
import { useIntl } from 'react-intl'
import { v4 as uuid } from 'uuid'

import SearchSatCodeWizardContainer from 'containers/Shared/SearchSatCodeWizardContainer/SearchSatCodeWizardContainer'

import Grid from '@material-ui/core/Grid'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableBody from '@material-ui/core/TableBody'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import Button from 'components/Shared/Button/Button'
import Dropdown from 'components/FormComponents/Dropdown/Dropdown'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import NumericField from 'components/FormComponents/IOComponents/NumericField/NumericField'
import SearchField from 'components/FormComponents/IOComponents/SearchField'
import AutocompleteField from 'components/FormComponents/IOComponents/AutocompleteField/AutoCompleteField'

import {
  createTax,
  deleteConcept,
  deleteTax,
  setConceptValue,
  updateTax,
} from 'actions/invoices'
import { searchInCatalogue } from 'actions/catalogues'

import { selectCatalogue } from 'lib/helpers/selectors'

import Delete from '@material-ui/icons/Delete'
import Search from '@material-ui/icons/Search'
import './styles.scss'

import { CatalogueEntry } from 'models/catalogues'
import { Tax, TaxCatalogue, TaxCatalogueEntry } from 'models/invoices.models'
import { isNumberInRange } from 'lib/helpers/ioValidations'
import { TAXES_FACTORS } from '../../../../constants'
import { IInvoiceCreationTaxRequest } from 'services/Invoices/invoices.models'

interface Props {
  impuestosCat: TaxCatalogue
  title: string
  uuid: string
  cantidad?: string
  clave?: CatalogueEntry | null
  descripcion?: string
  descuento?: string
  id?: number
  importacion?: string
  impuestos?: (Tax | IInvoiceCreationTaxRequest)[]
  objImp?: CatalogueEntry | null
  precio?: string
  unidad?: CatalogueEntry | null
  predial?: string
}

const AddIncomeCFDIConceptContainer = (props: Props) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const thunkDispatch = useThunkDispatch()

  const unidadesCat = useAppSelector(selectCatalogue('unidades'))
  const objImpCat = useAppSelector(selectCatalogue('objImp'))

  const [selectedTax, setSelectedTax] = useState<TaxCatalogueEntry | null>(null)
  const [rate, setRate] = useState<string>('')
  const [fee, setFee] = useState<string>('')
  const [openWizard, setOpenWizard] = useState(false)

  // const typedImpuestos = (props.impuestos as Tax[]).filter(({ type }) => type)
  const typedImpuestos = props.impuestos as Tax[]

  const addTax = useCallback(() => {
    if (!selectedTax) return

    const tax: Tax = {
      ...selectedTax,
      uuid: uuid(),
      selectedValue: selectedTax.factor === 'Tasa' ? Number(rate) : Number(fee),
    }
    dispatch(createTax(props.uuid, tax))
    setRate('')
    setFee('')
    setSelectedTax(null)
  }, [dispatch, fee, props.uuid, rate, selectedTax])

  const saveValueToStore = (name: string, value: any) => {
    dispatch(setConceptValue(props.uuid, name, value))
  }

  const getTaxValue = (factor: string, value: number) => {
    switch (factor) {
      case 'Tasa': {
        if (!value && value !== 0) return ''
        const formatted = intl.formatNumber(value, {
          style: 'percent',
          minimumFractionDigits: 2,
        })
        return formatted
      }
      case 'Cuota': {
        if (!value && value !== 0) return ''
        const formatted = intl.formatNumber(value, {
          style: 'currency',
          currency: 'MXN',
          currencyDisplay: 'narrowSymbol',
        })
        return formatted
      }
      default:
        return ''
    }
  }
  const renderCatalogueOption = (o: CatalogueEntry) => (
    <span className="catalogue-option">{`${o.value} - ${o.label}`}</span>
  )

  const isDisableAddTaxButton = useMemo(() => {
    if (!selectedTax) return true
    const { factor } = selectedTax
    if (factor === TAXES_FACTORS.exent) return false
    if (factor === TAXES_FACTORS.rate && rate !== '') return false
    if (factor === TAXES_FACTORS.fee && fee !== '') return false
    return true
  }, [selectedTax, rate, fee])

  useEffect(() => {
    if (props.impuestos) {
      for (const impuesto of props.impuestos) {
        const typedInputTax = impuesto as IInvoiceCreationTaxRequest
        if (typedInputTax.impuesto) {
          const taxCatalogueMatch = props.impuestosCat.find(
            ({ id }) => Number(typedInputTax.impuesto) === id
          )

          if (taxCatalogueMatch) {
            const tax: Tax = {
              ...taxCatalogueMatch,
              uuid: typedInputTax.uuid,
              selectedValue: typedInputTax.rate,
            }
            dispatch(updateTax(props.uuid, tax))
          }
        }
      }
    }
  }, [dispatch, props.impuestos, props.impuestosCat, props.uuid])

  return (
    <Dropdown
      id={`conceptDropdownBtn_${props.id}`}
      title={props.title}
      className="add-invoice-product-form"
      capitalize
      withDivider
      defaultExpanded>
      <Grid container spacing={4} className="basic-information">
        <Grid item xs={12} className="item-actions">
          <Button
            id={`deleteConceptBtn_${props.id}`}
            startIcon={<Delete />}
            onClick={() => dispatch(deleteConcept(props.uuid))}>
            Eliminar Concepto
          </Button>
        </Grid>
        <Grid item xs={4}>
          <SearchField
            name={`claveSat_${props.id}`}
            label="Clave SAT"
            onSearch={(v: string) =>
              thunkDispatch(searchInCatalogue('items', v))
            }
            value={props.clave}
            onChange={(v: CatalogueEntry) => saveValueToStore('claveSat', v)}
            getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
            renderOption={(o: any) => renderCatalogueOption(o)}
            InputProps={{
              endAdornment: (
                <IconButton
                  aria-label="satKeyOpenSearch"
                  onClick={() => setOpenWizard(true)}
                  size="small">
                  <Search />
                </IconButton>
              ),
            }}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <AutocompleteField
            name={`unidad_${props.id}`}
            label="Unidad o medida"
            catalogue={unidadesCat}
            value={props.unidad}
            onChange={(v: CatalogueEntry) =>
              saveValueToStore('claveUnitSat', v)
            }
            getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
            renderOption={(o: any) => renderCatalogueOption(o)}
            getOptionSelected={(
              option: CatalogueEntry,
              value: CatalogueEntry
            ) => option.idCatalogValues === value.idCatalogValues}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <AutocompleteField
            name={`objImp_${props.id}`}
            label="Objeto de Impuesto"
            catalogue={objImpCat}
            value={props.objImp}
            onChange={(v: CatalogueEntry) => saveValueToStore('objImp', v)}
            getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
            renderOption={(o: any) => renderCatalogueOption(o)}
            getOptionSelected={(
              option: CatalogueEntry,
              value: CatalogueEntry
            ) => option.idCatalogValues === value.idCatalogValues}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <NumericField
            name={`precioUnitario_${props.id}`}
            label="Precio unitario"
            value={props.precio}
            onValue={(v: string) => {
              saveValueToStore('price', v)
            }}
            onChange={(v: string) => v === '' && saveValueToStore('price', '')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            format="money"
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <NumericField
            name={`cantidad_${props.id}`}
            label="Cantidad"
            validate={['isMoreThanZero']}
            value={props.cantidad}
            onValue={(v: string) => saveValueToStore('cantidad', v)}
            onChange={(v: string) =>
              v === '' && saveValueToStore('cantidad', '')
            }
            format="money"
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            name={`predial_${props.id}`}
            label="Cuenta Predial"
            value={props.predial}
            onValue={(v: string) => saveValueToStore('predial', v)}
            inputProps={{ maxLength: 32 }}
            fullWidth
          />
        </Grid>
        <Grid item xs={8}>
          <p className="form-label">Descripción</p>
          <TextField
            variant="outlined"
            fieldType="alphanumericWithSpecialCharacters"
            name={`description_${props.id}`}
            placeholder="Descripción"
            value={props.descripcion}
            onValue={(v: string) => saveValueToStore('description', v)}
            onChange={(v: string) =>
              v === '' && saveValueToStore('description', '')
            }
            inputProps={{ maxLength: 500 }}
            multiline
            minRows={4}
            maxRows={4}
            withCounter
            fullWidth
            required
          />
        </Grid>
      </Grid>
      <Dropdown
        id={`impuestosDropdown_${props.id}`}
        title="impuestos"
        headerBackgroundColor="gray"
        bordered>
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <AutocompleteField
              name={`impuesto_${props.id}`}
              label="Impuesto"
              catalogue={props.impuestosCat}
              value={selectedTax}
              onChange={(v: any) => {
                setSelectedTax(v)
                setRate('')
                setFee('')
              }}
              getOptionLabel={(o: any) =>
                `${String(o.id).padStart(2, '0')} - ${o.label}`
              }
              fullWidth
              exclude
            />
          </Grid>
          {!!selectedTax &&
            Array.isArray(selectedTax?.value) &&
            selectedTax?.value.length > 1 && (
              <Grid item xs={4}>
                <AutocompleteField
                  name={`tasa_${props.id}`}
                  label="Tasa (%)"
                  catalogue={selectedTax?.value.map(v => ({
                    label: `${v * 100}%`,
                    value: v,
                  }))}
                  value={
                    rate !== ''
                      ? { label: `${Number(rate) * 100}%`, value: rate }
                      : undefined
                  }
                  onChange={(v: any) => setRate(v ? v.value : '')}
                  getOptionLabel={(o: any) => o.label}
                  fullWidth
                  exclude
                />
              </Grid>
            )}
          {!!selectedTax && !Array.isArray(selectedTax?.value) && (
            <Grid item xs={4}>
              <NumericField
                name={`cuota_${props.id}`}
                label="Cuota"
                value={fee}
                onChange={(v: string) => setFee(v)}
                validate={(v: string) => {
                  const minOperand =
                    selectedTax?.value &&
                    typeof selectedTax.value === 'object' &&
                    'min' in selectedTax.value
                      ? selectedTax.value.min
                      : 0
                  const maxOperand =
                    selectedTax?.value &&
                    typeof selectedTax.value === 'object' &&
                    'max' in selectedTax.value
                      ? selectedTax.value.max
                      : 0

                  const min =
                    Number(props.cantidad) * Number(props.precio) * minOperand
                  const max =
                    Number(props.cantidad) * Number(props.precio) * maxOperand
                  return isNumberInRange(v, min, max)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                format="money"
                fullWidth
                exclude
              />
            </Grid>
          )}
          <Grid item xs={4}>
            <Button
              id={`conceptAddTaxBtn_${props.id}`}
              variant="outlined"
              color="warning"
              disabled={isDisableAddTaxButton}
              onClick={addTax}>
              Agregar
            </Button>
          </Grid>
          {typedImpuestos.length > 0 && (
            <Grid item xs={12} className="table-taxes">
              <Table size="small" aria-label="taxes-table">
                <TableHead>
                  <TableRow className="table-header">
                    <TableCell
                      className="table-cell"
                      style={{ width: '150px' }}>
                      Tipo de impuesto
                    </TableCell>
                    <TableCell
                      className="table-cell"
                      style={{ width: '300px' }}>
                      Impuesto
                    </TableCell>
                    <TableCell
                      className="table-cell"
                      style={{ width: '150px' }}>
                      Tasa o cuota
                    </TableCell>
                    <TableCell className="table-cell"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {typedImpuestos.map(i => (
                    <TableRow className="table-row" key={i.uuid}>
                      <TableCell className="table-cell">{i.type}</TableCell>
                      <TableCell className="table-cell">
                        {`
                          ${String(i.sat?.value ?? '').padStart(3, '0')}
                          -
                          ${i.sat?.label ?? ''}
                        `}
                      </TableCell>
                      <TableCell className="table-cell">
                        {getTaxValue(i.factor, i.selectedValue) || 'N/A'}
                      </TableCell>
                      <TableCell className="table-cell table-actions">
                        <IconButton
                          aria-label="delete-tax"
                          id={`conceptDeleteTaxBtn_${props.id}_${i}`}
                          onClick={() =>
                            dispatch(deleteTax(props.uuid, i.uuid))
                          }
                          size="small">
                          <Delete className="delete" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          )}
        </Grid>
      </Dropdown>
      <SearchSatCodeWizardContainer
        open={openWizard}
        onSelect={(v: CatalogueEntry) => saveValueToStore('claveSat', v)}
        onCancel={() => setOpenWizard(false)}
      />
    </Dropdown>
  )
}

export default AddIncomeCFDIConceptContainer
